<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import qs from 'qs';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsLoader from '@/components/EsLoader.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsException from '@/components/EsException.vue';
import EsPager from '@/components/EsPager.vue';
import EsSelect from '@/components/EsSelect.vue';
import PidNearMatchResults from '@/components/PidNearMatchResults.vue';
import PidLegendNearMatch from '@/components/PidLegendNearMatch.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
);

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    FieldLabels,
  ],
  components: {
    EsLoader,
    EsPager,
    EsSelect,
    EsException,
    EsFilter,
    PidLegendNearMatch,
    PidNearMatchResults,
  },
  props: {
    districtCode: {
      type: Array,
    },
    pidSubmissionId: {
      type: String,
    },
    pidBatchId: {
      type: String,
    },
    endDate: {
      type: String,
    },
    lastName: {
      type: String,
    },
    identifier: {
      type: String,
    },
    page: {
      type: Number,
    },
    schoolCode: {
      type: Array,
    },
    schoolYear: {
      type: Array,
    },
    startDate: {
      type: String,
    },
    submissionType: {
      type: Array,
    },
    sortField: {
      type: String,
      default: 'nmPercent',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    return {
      personType: null,
      filtered: false,
      filterExpanded: null,

      loadedDistrictSchools: {},
      loading: true,
      loadingError: null,
      loadingNearMatches: null,
      loadingNearMatchError: null,
      typeQueryParam: { search: true },
      nearMatches: [],
      nearMatchCount: null,
      submissionCount: null,
      totalCount: null,

      // getting values from props
      batchIdInput: this.pidBatchId,
      districts: this.districtCode.map(d => ({ code: d })),
      endDateInput: this.endDate,
      idInput: this.identifier,
      identifierType: null,
      identifierTypes: [],
      lastNameInput: this.lastName,
      transactionIdInput: this.pidSubmissionId,
      startDateInput: this.startDate,
      schools: this.schoolCode.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: null,
      submissionTypes: [],

      sorterOptions: [],
      sortValueInput: null,
      years: [],

      lowerBound: null,
      upperBound: null,
      /**
       * Ideally we want this to be coming from and api.
       * However, we can pass this array into the es-select component.
       */
    };
  },
  async created() {
    // TODO: handle filter open/closed
    this.filterExpanded = true;
    // get default person type

    this.setBreadcrumb([
      { text: 'Home', to: { name: 'personId' } },
      { text: `Pending ${this.properties['SYSTEM_LABEL-NEARMATCH']}`, to: { } },
    ]);
    try {
      await Promise.all([
        this.loadRaces(),
        this.loadGenders(),
        this.loadPidSchoolYears(),
        this.loadPersonTypes(),
      ]);
      this.loadingNearMatchError = false;
      this.loadingNearMatches = true;
      this.years = this.pidSchoolYearsDesc;
      this.schoolYearInput = this.years.filter(y => this.schoolYear.find(sy => sy === y.year));
      this.personType = this.defaultPersonType;
    } catch (error) {
      this.loadingNearMatchError = true;
      this.handleMatchError(error);
    } finally {
      this.loading = false;
      this.loadingNearMatches = false;
    }
    if (this.hasPropIds) {
      this.onLoad();
    }
    this.identifierTypes = [
      {
        code: 'S',
        description: 'State ID',
      },
      {
        code: 'L',
        description: 'Local ID',
      },
    ];
    this.sorterOptions = [
      {
        description: 'Max Match Score Desc',
        field: 'nmPercent',
        order: 'DESC',
        id: 4,
      },
      // Match
      {
        description: 'Max Match Score Asc',
        field: 'nmPercent',
        order: 'ASC',
        id: 3,
      },
      // Match
      {
        description: 'Last Name Asc',
        field: 'nmLastName',
        order: 'ASC',
        id: 2,
      },
      {
        description: 'Last Name Desc',
        field: 'nmLastName',
        order: 'DESC',
        id: 1,
      },
    ];
    this.sortValueInput = this.sorterOptions
      .filter(sort => sort.field === this.sortField && sort.order === this.sortOrder);
  },
  mounted() {
    this.checkNotification();
  },
  watch: {
    $route() {
      if (this.hasPropIds) {
        this.reloadMatches();
      }
    },
    districts() {
      this.$refs.selSchools.clear();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'pidSchoolYearsDesc',
      'defaultPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    disabledSearchButton() {
      return !this.transactionIdInput && !this.batchIdInput;
    },
    hasPropIds() {
      return this.pidSubmissionId || this.pidBatchId;
    },
    secondarySort() {
      let field;
      let order;
      if (this.sortField === 'nmPercent') {
        field = 'nmLastName';
        order = 'ASC';
      } else {
        field = 'nmPercent';
        order = 'DESC';
      }

      return { field, order };
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    hasParams() {
      return this.districtCode.length || this.pidSubmissionId || this.pidBatchId
      || this.page || this.schoolCode.length || this.schoolYear.length;
    },
    hasInputParams() {
      return this.batchIdInput || this.districts.length || this.endDateInput
      || this.lastNameInput || this.page || this.schools.length || this.schoolYearInput.length
      || this.startDateInput || this.transactionIdInput || this.idInput
      || this.identifierType.length || this.submissionTypes.length;
    },
    getInputIdentifierType() {
      return (this.identifierType && this.identifierType.length)
        ? (this.identifierType.map(p => p.code))[0] : 'S';
    },
    defaultIdentifierType() {
      return (this.identifierTypes && this.identifierTypes.length)
        ? (this.identifierTypes.filter(p => p.code === 'S')) : undefined;
    },
    getUrlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.pidSubmissionId = this.pidSubmissionId ? this.pidSubmissionId : undefined;
      params.pidBatchId = this.pidBatchId ? this.pidBatchId : undefined;
      params.districtCodes = this.districtCode.length ? this.districtCode : undefined;
      params.schoolCodes = this.schoolCode.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.lastName = this.lastNameInput ? this.lastNameInput.trim() : undefined;
      params.identifierType = this.getInputIdentifierType;
      params.submissionTypes = this.submissionTypes.length
        ? this.submissionTypes.map(c => c.code) : undefined;
      params.startDate = this.startDate ? this.startDate : undefined;
      params.endDate = this.endDate ? this.endDate : undefined;
      params.open = true;

      params.sort = JSON.stringify([
        {
          field: this.sortField,
          order: this.sortOrder,
        },
        this.secondarySort,
      ]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadRaces',
      'loadGenders',
      'loadPidSchoolYears',
      'loadPersonTypes',
    ]),
    checkNotification() {
      const { message } = this.$route.params;
      if (this.$route.params.message) {
        this.showNotification('success', [message]);
      }
    },
    checkStateIdInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except Integers and (-)
      if ((charCode < 48 || charCode > 57) && charCode !== 45) {
        evt.preventDefault();
      }
    },
    async reloadMatches() {
      try {
        this.loadingNearMatches = true;
        this.loadingNearMatchError = false;
        this.nearMatches = [];
        await this.getMatches();
      } catch (error) {
        this.loadingNearMatchError = true;
        this.handleMatchError(error);
      } finally {
        this.loadingNearMatches = false;
      }
    },
    async getMatches() {
      try {
        if (!this.filtered && this.hasParams) {
          this.filtered = true;
        }
        const res = await axios.get('personid/transactions/details?getDetails=true',
          {
            params: this.getUrlParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        const { data: { data: temp, totalCount } } = res;
        if (typeof this.idInput !== 'string' || this.idInput.trim() === '') {
          this.nearMatches = temp;
          this.totalCount = totalCount;
        } else {
          const filteredData = temp.reduce((acc, item) => {
            const filteredMatches = item.pidMatchTransDetail.response.potentialMatches
              .filter(match => match.id === this.idInput);
            if (filteredMatches.length > 0) {
              acc.push({
                ...item,
                pidMatchTransDetail: {
                  ...item.pidMatchTransDetail,
                  response: {
                    ...item.pidMatchTransDetail.response,
                    potentialMatches: filteredMatches,
                  },
                },
              });
            }
            return acc;
          }, []);
          this.nearMatches = filteredData;
          this.totalCount = filteredData.length;
        }
      } catch (error) {
        this.loadingNearMatchError = true;
        this.handleMatchError(error);
      }
    },
    handleMatchError(error) {
      const message = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
      this.showNotification('danger', [message]);
    },
    filterData() {
      this.$router.replace({ query: this.searchParams() });
    },
    searchParams() {
      const params = {};

      if (this.transactionIdInput) {
        params.pidSubmissionId = this.transactionIdInput.trim();
      }

      if (this.batchIdInput) {
        params.pidBatchId = this.batchIdInput.trim();
      }

      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }

      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }

      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }

      if (this.submissionTypes) {
        params.submissionTypes = this.submissionTypes.map(c => c.code);
      }

      if (this.lastNameInput) {
        params.lastName = this.lastNameInput.trim();
      }

      if (this.idInput) {
        params.personIdentifier = this.idInput.trim().replace(/-/g, '');
      }

      params.identifierType = this.getInputIdentifierType;

      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }

      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }

      params.sortField = this.sortValueInput[0].field;
      params.sortOrder = this.sortValueInput[0].order;

      return params;
    },
    resetFilter() {
      if (this.filtered) {
        this.filtered = false;
      }

      if (this.hasParams) {
        this.$router.replace({ query: {} });
      }
      this.$refs.selIdentifierTypes.clear();
      this.$refs.selSubmissionType.clear();
      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selYears.clear();
      this.$refs.selSorter.clear();

      this.batchIdInput = '';
      this.idInput = '';
      this.lastNameInput = '';
      this.transactionIdInput = '';
      this.startDateInput = '';
      this.endDateInput = '';

      this.$refs.selIdentifierTypes.pushValues(this.defaultIdentifierType);

      this.$refs.selSorter.pushValues(
        this.sorterOptions
          .filter(sort => sort.field === 'nmPercent' && sort.order === 'DESC'),
      );

      this.totalCount = null;
      this.totalNearMatchCount = null;

      this.nearMatches = [];
    },
    /**
     * Parses the school codes and district codes in the url.
     */
    getSchoolCodes() {
      return this.schoolCode.map(code => code.split(/[:]+/)[0]);
    },
    async onLoad() {
      try {
        this.loadingNearMatches = true;
        this.loadingNearMatchError = false;
        await this.getMatches();
        await this.loadPersonTypes();
      } catch (e) {
        this.loadingNearMatchError = true;
      } finally {
        this.loadingNearMatches = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <es-loader v-if="loading" style="margin: 1rem"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else class="document-container">
      <div class="document">
        <div class="near-match-box">
          <div class="header-row is-flex">
            <div class="warning is-flex flex-align--center flex-justify--center">
              <font-awesome-icon
                class="has-text-white fa-2x"
                :icon="['fas', 'exclamation-triangle']"
              />
            </div>
            <div class="header-row--heading is-flex">
              <div class="is-flex header-row--description grow">
                <div
                  class="is-flex header-row--cell flex-align--center
                    has-text-weight-bold is-size-4 nowrap uppercase"
                >
                  {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(S/ES) FOUND:
                </div>
                <div class="header-row--cell grow has-text-weight-bold has-text-left border--left">
                  <div class="font-col">
                    The system found {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                    which should be reviewed and resolved by a user.
                  </div>
                  <div class="">
                    Next Steps: Review {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es) below
                  </div>
                </div>
              </div>
              <div
                class="is-flex header-row--cell flex-direction--column
                  flex-justify--center border--left"
              >
                <div
                  v-if="loadingNearMatches"
                  class="has-text-weight-bold is-size-5"
                >
                  --
                </div>
                <div
                  v-else
                  class="has-text-weight-bold is-size-5"
                >
                  {{ totalCount }}
                </div>
                <div class="nowrap is-size-7 has-text-grey">
                  {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                </div>
              </div>
            </div>
          </div>
          <es-filter :default-expanded="filterExpanded" :bottom-border="false">
            <template #filter-options>
              <div class="columns is-3-desktop filter-section multi-dropdown-columns">
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold" for="filterTransIdControl">
                      Transaction ID :
                    </label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        v-model="transactionIdInput"
                        id="filterTransIdControl"
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold" for="filterBatchIdControl">
                      Batch ID :
                    </label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        v-model="batchIdInput"
                        id="filterBatchIdControl"
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <es-select
                    api="submission-types"
                    key-value="code"
                    label="Submission Type : "
                    :clearOut="true"
                    :multi="true"
                    ref="selSubmissionType"
                    value-tpl="${description}"
                    @update:values="submissionTypes = $event"
                    controlId="filterSubmissionTypeControl"
                  />
                </div>
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold" for="filterLastNameControl">
                      Last Name :
                    </label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        v-model="lastNameInput"
                        id="filterLastNameControl"
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold" for="filterIdentifierControl">
                      Identifier :
                    </label>
                    <div class="control">
                      <input
                        class="input"
                        type="text"
                        @keypress="checkStateIdInput($event)"
                        v-model="idInput"
                        id="filterIdentifierControl"
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <es-select
                    label="Identifier Type :"
                    valueTpl="${description}"
                    keyValue="code"
                    v-on:update:values="identifierType = $event"
                    ref="selIdentifierTypes"
                    :setValues="identifierTypes"
                    :pre-selected="defaultIdentifierType"
                    controlId="filterIdentifierTypeControl"
                  />
                </div>
              </div>
              <div class="columns is-3-desktop filter-section multi-dropdown-columns">
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`)} :`"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="districts"
                    v-on:update:values="districts = $event"
                    ref="selDistricts"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    :preSelected="districtCode"
                    searchMinLength="2"
                    keyValue="code"
                    controlId="filterDistrictControl"
                  />
                </div>
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`)} :`"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="schools"
                    :apiQueryParams="getDistrictParams"
                    v-on:update:values="schools = $event"
                    ref="selSchools"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    :subValueTplFn="(result) =>
                      `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                    searchMinLength="2"
                    :key-value="['districtCode', 'code']"
                    controlId="filterSchoolControl"
                  />
                </div>
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_YEAR`)} :`"
                    v-bind:clearOut="true"
                    valueTpl="${year}"
                    keyValue="id"
                    v-on:update:values="schoolYearInput = $event"
                    ref="selYears"
                    :preSelected="schoolYearInput"
                    :setValues="years"
                    controlId="filterSchoolYearControl"
                  />
                </div>
                <div class="column is-4">
                  <div class="field no-border">
                    <label class="label has-text-weight-bold">Process Date Range :</label>
                    <div class="control is-flex">
                      <input
                        class="input"
                        type="date"
                        v-model="startDateInput"
                        aria-label="Start Date Input"
                      >
                      <span
                        class="is-flex flex-align--center"
                        style="padding: 0 0.5rem; font-size: 12px"
                      >
                        to
                      </span>
                      <input
                        class="input"
                        type="date"
                        v-model="endDateInput"
                        aria-label="End Date Input"
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <es-select
                    label="Sort By :"
                    valueTpl="${description}"
                    keyValue="id"
                    v-on:update:values="sortValueInput = $event"
                    ref="selSorter"
                    :preSelected="sortValueInput"
                    :setValues="sorterOptions"
                    controlId="filterSortByControl"
                  />
                </div>
              </div>
              <div class="field is-grouped flex-justify--end">
                <div class="control is-flex flex-align--center">
                  <a @click="resetFilter" @keypress="resetFilter" role="button" tabindex="0">
                    Reset
                  </a>
                </div>
                <div class="control">
                  <button
                    class="button is-primary"
                    @click="filterData"
                    :disabled="disabledSearchButton"
                    :title="disabledSearchButton ? 'Transaction ID or Batch ID is required.' : null"
                    >
                    Filter Data
                  </button>
                </div>
              </div>
            </template>
          </es-filter>
        </div>
      </div>
      <div class="match-title is-flex has-text-grey"
        style="margin-top: -1rem"
      >
        Pending {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
      </div>
      <es-exception v-if="!hasPropIds" style="padding: 6.5rem" margin="0rem"
        exceptionMessage="Batch ID or Transaction ID must be entered to view results."
      />
      <es-loader v-else-if="loadingNearMatches" style="margin: 6.5rem"/>
      <es-exception v-else-if="loadingNearMatchError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception v-else-if="!nearMatches.length"
        exceptionMessage="No results were found."
      />
      <template v-else-if="nearMatches.length">
        <pid-near-match-results
          v-for="(match, index) in nearMatches"
          :loaded-district-schools="loadedDistrictSchools"
          :near-match="match"
          @refresh="reloadMatches"
          v-bind:key="index"
        />
        <table class="table is-fullwidth" role="presentation">
          <tfoot style="background-color: #fff">
            <tr>
              <td colspan="100%">
                <es-pager :totalCount="totalCount"/>
              </td>
            </tr>
          </tfoot>
        </table>
      </template>
      <pid-legend-near-match v-if="!loadingNearMatches && nearMatches.length"/>
    </div>
  </div>
</template>


<style lang="sass" scoped>
  @import "~bulma/sass/utilities/mixins"
  +touch
    .header-row--description
      flex-direction: column
      border-left: none
  .document-container
    margin-left: auto
    margin-right: auto
  .header-row--heading
    width: 100%
    padding: 0.5rem 0
    border-bottom: 1px solid #eee
  .header-row--cell
    padding: 0 1rem
  .header-row--button
    padding: 1rem
  .border--left
    border-left: 1px solid #ccc
  .match-title
    padding: 1rem 0 1rem
    color: #6E6E6E !important
  .header-row
    min-height: 60px
    .warning
      background-color: #DC3545
      min-height: 60px
      min-width: 60px
  .border-right--dashed
    border-right: 1px dashed #ccc
  .filter-row--container
    padding-right: 1rem
  .is-fullwidth
    width: 100%

  .filter-box
    min-width: 190px
    padding: 0 1rem
    margin-bottom: 1rem
  .no-padding
    padding: 0

  .font-col
    color: #767676

  .uppercase
    text-transform: uppercase

</style>
